import { z } from 'zod';
import type { FetchLike } from './config';
import { errorMessage } from './utils';

/**
 * Thin fetch wrapper for the ServiceTim API.
 *
 * - Prefixes paths with the configured base URL (`/api` by default).
 * - Sends/receives JSON and a bearer token when the session has one.
 * - Turns RFC 9457 `application/problem+json` error bodies into `ApiError`.
 * - Optional response validation through any `{ parse }` object (a Zod schema fits).
 */

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

/** Lenient mirror of the API's problem+json body; unknown extension members are kept. */
export const ProblemDetailsSchema = z.looseObject({
  type: z.string().optional(),
  title: z.string().optional(),
  status: z.number().int().optional(),
  /** Stable machine-readable code, e.g. `validation_failed`, `version_mismatch`. */
  code: z.string().optional(),
  detail: z.string().optional(),
  instance: z.string().optional(),
  errors: z.array(z.object({ path: z.string(), message: z.string() })).optional(),
});
export type ProblemDetails = z.infer<typeof ProblemDetailsSchema>;

export interface ApiErrorInit {
  status: number;
  url: string;
  message: string;
  code?: string | null;
  problem?: ProblemDetails | null;
}

export class ApiError extends Error {
  /** HTTP status; `0` when the request never reached the server. */
  readonly status: number;
  readonly url: string;
  readonly code: string | null;
  readonly problem: ProblemDetails | null;

  constructor(init: ApiErrorInit) {
    super(init.message);
    this.name = 'ApiError';
    this.status = init.status;
    this.url = init.url;
    this.code = init.code ?? null;
    this.problem = init.problem ?? null;
  }

  get isNetworkError(): boolean {
    return this.status === 0;
  }

  /** Field-level validation errors (`validation_failed`), or an empty list. */
  get fieldErrors(): ReadonlyArray<{ path: string; message: string }> {
    return this.problem?.errors ?? [];
  }

  static fromResponse(response: Response, url: string, payload: unknown): ApiError {
    const parsed = ProblemDetailsSchema.safeParse(payload);
    const problem = parsed.success ? parsed.data : null;
    const fallback = `${response.status} ${response.statusText}`.trim();
    return new ApiError({
      status: response.status,
      url,
      message: problem?.detail ?? problem?.title ?? fallback,
      code: problem?.code ?? null,
      problem,
    });
  }
}

export interface Parser<T> {
  parse: (data: unknown) => T;
}

export type QueryParams = Record<string, string | number | boolean | null | undefined>;

export interface RequestOptions<T> {
  body?: unknown;
  query?: QueryParams;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Validates/narrows the JSON response; without it the body is trusted as `T`. */
  schema?: Parser<T>;
}

export interface WriteOptions<T> extends Omit<RequestOptions<T>, 'body'> {
  /** Sent as `Idempotency-Key`; the API de-duplicates POSTs carrying the same key. */
  idempotencyKey?: string;
  /** Sent as `If-Match`; the API rejects PATCHes whose version no longer matches. */
  ifMatch?: string | number;
}

export interface ApiClientOptions {
  baseUrl: string;
  /** Returns the current access token, or `null` to send the request anonymously. */
  getAccessToken?: () => string | null;
  /** Headers added to every request (e.g. `x-dev-claims` in local dev-claims mode). */
  defaultHeaders?: Record<string, string>;
  fetch?: FetchLike;
}

const defaultFetch: FetchLike = (input, init) => fetch(input, init);
const noToken = (): string | null => null;

export class ApiClient {
  readonly baseUrl: string;
  private readonly getAccessToken: () => string | null;
  private readonly defaultHeaders: Record<string, string>;
  private readonly fetchImpl: FetchLike;

  constructor(options: ApiClientOptions) {
    this.baseUrl = options.baseUrl.replace(/\/+$/, '');
    this.getAccessToken = options.getAccessToken ?? noToken;
    this.defaultHeaders = options.defaultHeaders ?? {};
    this.fetchImpl = options.fetch ?? defaultFetch;
  }

  get<T>(path: string, options: RequestOptions<T> = {}): Promise<T> {
    return this.request<T>('GET', path, options);
  }

  post<T>(path: string, body: unknown, options: WriteOptions<T> = {}): Promise<T> {
    return this.request<T>('POST', path, { ...options, body, headers: writeHeaders(options) });
  }

  patch<T>(path: string, body: unknown, options: WriteOptions<T> = {}): Promise<T> {
    return this.request<T>('PATCH', path, { ...options, body, headers: writeHeaders(options) });
  }

  put<T>(path: string, body: unknown, options: WriteOptions<T> = {}): Promise<T> {
    return this.request<T>('PUT', path, { ...options, body, headers: writeHeaders(options) });
  }

  delete<T = void>(path: string, options: RequestOptions<T> = {}): Promise<T> {
    return this.request<T>('DELETE', path, options);
  }

  async request<T>(method: HttpMethod, path: string, options: RequestOptions<T> = {}): Promise<T> {
    const url = this.resolveUrl(path, options.query);
    const headers = new Headers({ ...this.defaultHeaders, ...options.headers });
    headers.set('Accept', 'application/json, application/problem+json');
    if (options.body !== undefined) {
      headers.set('Content-Type', 'application/json');
    }
    const token = this.getAccessToken();
    if (token !== null && token.length > 0) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    let response: Response;
    try {
      response = await this.fetchImpl(url, {
        method,
        headers,
        body: options.body === undefined ? undefined : JSON.stringify(options.body),
        signal: options.signal,
      });
    } catch (error) {
      // Cancellation is not an API failure; let TanStack Query (or the caller) see the abort.
      if (options.signal?.aborted) throw error;
      throw new ApiError({
        status: 0,
        url,
        message: `Network error calling ${method} ${url}: ${errorMessage(error)}`,
      });
    }

    const payload = await readBody(response);
    if (!response.ok) {
      throw ApiError.fromResponse(response, url, payload);
    }
    return options.schema ? options.schema.parse(payload) : (payload as T);
  }

  resolveUrl(path: string, query?: QueryParams): string {
    const isAbsolute = /^https?:\/\//.test(path);
    const base = isAbsolute ? path : `${this.baseUrl}${path.startsWith('/') ? '' : '/'}${path}`;
    if (!query) return base;
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null) params.set(key, String(value));
    }
    const qs = params.toString();
    return qs.length > 0 ? `${base}${base.includes('?') ? '&' : '?'}${qs}` : base;
  }
}

function writeHeaders<T>(options: WriteOptions<T>): Record<string, string> {
  const headers: Record<string, string> = { ...options.headers };
  if (options.idempotencyKey !== undefined) headers['Idempotency-Key'] = options.idempotencyKey;
  if (options.ifMatch !== undefined) headers['If-Match'] = String(options.ifMatch);
  return headers;
}

/** Reads the body once: JSON when the content type says so, otherwise text; `undefined` when empty. */
async function readBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (text.length === 0) return undefined;
  const contentType = response.headers.get('content-type') ?? '';
  if (/[/+]json\b/i.test(contentType)) {
    try {
      return JSON.parse(text) as unknown;
    } catch {
      return text;
    }
  }
  return text;
}
